/**
 * Image Compression Utility
 * 
 * Handles automatic compression of large images/screenshots
 * to ensure they can be uploaded without exceeding server limits.
 */

export interface CompressionOptions {
  maxSizeMB?: number;
  initialQuality?: number;
  maxDimension?: number;
  format?: 'auto' | 'png' | 'jpeg';
  minQuality?: number;
}

export interface CompressionResult {
  dataUrl: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  format: string;
  quality: number;
  dimensions: {
    width: number;
    height: number;
  };
}

/**
 * Main image compression function
 */
export const compressImage = async (
  dataUrl: string,
  options: CompressionOptions = {}
): Promise<string> => {
  const {
    maxSizeMB = 0.8,
    initialQuality = 0.85,
    maxDimension = 1920,
    format = 'auto',
    minQuality = 0.3
  } = options;

  try {
    const originalSize = dataUrl.length;
    const originalSizeMB = originalSize / (1024 * 1024);

    console.log("🗜️ Starting image compression:", {
      originalSize: originalSizeMB.toFixed(2) + "MB",
      targetSize: maxSizeMB + "MB",
      initialQuality,
      maxDimension,
      format
    });

    // Convert data URL to blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    // If already small enough, return original
    if (blob.size <= maxSizeMB * 1024 * 1024) {
      console.log("✅ Image already within size limit");
      return dataUrl;
    }

    // Load image
    const img = await loadImage(dataUrl);

    console.log("📐 Original image dimensions:", {
      width: img.width,
      height: img.height,
      aspectRatio: (img.width / img.height).toFixed(2)
    });

    // Calculate new dimensions
    const { width, height } = calculateOptimalDimensions(
      img.width,
      img.height,
      maxDimension
    );

    console.log("📐 Target dimensions:", { width, height });

    // Create canvas
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    // ✅ FIX: Proper type assertion for CanvasRenderingContext2D
    const ctx = canvas.getContext("2d", {
      alpha: false,
      desynchronized: false,
      willReadFrequently: false,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    }) as CanvasRenderingContext2D | null;

    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }

    // Fill white background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    // Draw image
    ctx.drawImage(img, 0, 0, width, height);

    // Determine format
    const targetFormat = determineOptimalFormat(format, originalSizeMB, dataUrl);

    console.log("🎨 Using format:", targetFormat);

    // Compress with quality adjustment
    const compressedDataUrl = await compressWithQualityAdjustment(
      canvas,
      targetFormat,
      initialQuality,
      minQuality,
      maxSizeMB
    );

    // Log results
    const compressedSize = compressedDataUrl.length;
    const compressedSizeMB = compressedSize / (1024 * 1024);
    const reductionPercent = ((originalSize - compressedSize) / originalSize) * 100;

    console.log("✅ Image compressed successfully:", {
      originalSize: originalSizeMB.toFixed(2) + "MB",
      compressedSize: compressedSizeMB.toFixed(2) + "MB",
      reduction: reductionPercent.toFixed(1) + "%",
      format: targetFormat
    });

    return compressedDataUrl;

  } catch (error) {
    console.error("❌ Image compression failed:", error);
    console.warn("⚠️ Returning original image due to compression error");
    return dataUrl;
  }
};

/**
 * Compress image and return detailed result
 */
export const compressImageWithDetails = async (
  dataUrl: string,
  options: CompressionOptions = {}
): Promise<CompressionResult> => {
  const originalSize = dataUrl.length;
  const compressedDataUrl = await compressImage(dataUrl, options);
  const compressedSize = compressedDataUrl.length;

  // Detect format from data URL
  const formatMatch = compressedDataUrl.match(/data:image\/(\w+);/);
  const format = formatMatch ? formatMatch[1] : 'unknown';

  // Get dimensions
  const img = await loadImage(compressedDataUrl);

  return {
    dataUrl: compressedDataUrl,
    originalSize,
    compressedSize,
    compressionRatio: originalSize / compressedSize,
    format,
    quality: options.initialQuality || 0.85,
    dimensions: {
      width: img.width,
      height: img.height
    }
  };
};

/**
 * Load image from data URL
 */
const loadImage = (dataUrl: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      if (img.width === 0 || img.height === 0) {
        reject(new Error("Image has zero dimensions"));
        return;
      }
      resolve(img);
    };
    
    img.onerror = (error) => {
      reject(new Error(`Failed to load image: ${error}`));
    };
    
    img.src = dataUrl;
  });
};

/**
 * Calculate optimal dimensions while maintaining aspect ratio
 */
const calculateOptimalDimensions = (
  originalWidth: number,
  originalHeight: number,
  maxDimension: number
): { width: number; height: number } => {
  let width = originalWidth;
  let height = originalHeight;

  if (width > maxDimension || height > maxDimension) {
    if (width > height) {
      height = Math.round((height / width) * maxDimension);
      width = maxDimension;
    } else {
      width = Math.round((width / height) * maxDimension);
      height = maxDimension;
    }
  }

  return { width, height };
};

/**
 * Determine optimal format
 */
const determineOptimalFormat = (
  formatOption: 'auto' | 'png' | 'jpeg',
  sizeMB: number,
  dataUrl: string
): 'image/png' | 'image/jpeg' => {
  if (formatOption === 'png') return 'image/png';
  if (formatOption === 'jpeg') return 'image/jpeg';

  // Auto detection
  if (sizeMB > 2) {
    console.log("📊 Auto-selecting JPEG (large image)");
    return 'image/jpeg';
  }

  if (dataUrl.startsWith('data:image/jpeg')) {
    console.log("📊 Auto-selecting JPEG (original is JPEG)");
    return 'image/jpeg';
  }

  if (sizeMB < 1) {
    console.log("📊 Auto-selecting PNG (small image)");
    return 'image/png';
  }

  console.log("📊 Auto-selecting JPEG (default for compression)");
  return 'image/jpeg';
};

/**
 * Compress with progressive quality reduction
 */
const compressWithQualityAdjustment = async (
  canvas: HTMLCanvasElement,
  format: 'image/png' | 'image/jpeg',
  initialQuality: number,
  minQuality: number,
  targetSizeMB: number
): Promise<string> => {
  let currentQuality = initialQuality;
  let attempts = 0;
  const maxAttempts = 10;

  while (currentQuality >= minQuality && attempts < maxAttempts) {
    attempts++;

    const dataUrl = canvas.toDataURL(format, currentQuality);
    const sizeMB = dataUrl.length / (1024 * 1024);

    console.log(`🔄 Compression attempt ${attempts}:`, {
      quality: currentQuality.toFixed(2),
      size: sizeMB.toFixed(2) + "MB",
      target: targetSizeMB + "MB",
      withinTarget: sizeMB <= targetSizeMB
    });

    if (sizeMB <= targetSizeMB) {
      console.log(`✅ Target size achieved at quality ${currentQuality.toFixed(2)}`);
      return dataUrl;
    }

    const reductionStep = sizeMB > targetSizeMB * 2 ? 0.15 : 0.1;
    currentQuality -= reductionStep;
  }

  console.warn("⚠️ Could not reach target size with quality adjustment");
  console.log("🔨 Attempting extreme compression...");

  return extremeCompression(canvas, format, targetSizeMB);
};

/**
 * Extreme compression as last resort
 */
const extremeCompression = (
  canvas: HTMLCanvasElement,
  format: 'image/png' | 'image/jpeg',
  targetSizeMB: number
): string => {
  const smallCanvas = document.createElement("canvas");
  smallCanvas.width = Math.round(canvas.width * 0.7);
  smallCanvas.height = Math.round(canvas.height * 0.7);

  // ✅ FIX: Proper type assertion for CanvasRenderingContext2D
  const ctx = smallCanvas.getContext("2d") as CanvasRenderingContext2D | null;
  
  if (!ctx) {
    return canvas.toDataURL(format, 0.3);
  }

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, smallCanvas.width, smallCanvas.height);
  ctx.drawImage(canvas, 0, 0, smallCanvas.width, smallCanvas.height);

  const dataUrl = smallCanvas.toDataURL(format, 0.3);
  const sizeMB = dataUrl.length / (1024 * 1024);

  console.log("🔨 Extreme compression result:", {
    dimensions: `${smallCanvas.width}x${smallCanvas.height}`,
    size: sizeMB.toFixed(2) + "MB",
    quality: 0.3
  });

  return dataUrl;
};

/**
 * Check if image needs compression
 */
export const needsCompression = (
  dataUrl: string,
  maxSizeMB: number = 0.8
): boolean => {
  const sizeMB = dataUrl.length / (1024 * 1024);
  return sizeMB > maxSizeMB;
};

/**
 * Get image size in MB
 */
export const getImageSizeMB = (dataUrl: string): number => {
  return dataUrl.length / (1024 * 1024);
};

/**
 * Batch compress multiple images
 */
export const compressImages = async (
  dataUrls: string[],
  options: CompressionOptions = {}
): Promise<string[]> => {
  console.log(`🗜️ Starting batch compression of ${dataUrls.length} images`);
  
  const results = await Promise.all(
    dataUrls.map((dataUrl, index) => 
      compressImage(dataUrl, options).catch(error => {
        console.error(`❌ Failed to compress image ${index + 1}:`, error);
        return dataUrl; // Return original on error
      })
    )
  );

  console.log("✅ Batch compression complete");
  return results;
};

/**
 * Validate data URL format
 */
export const isValidDataUrl = (dataUrl: string): boolean => {
  return (
    typeof dataUrl === 'string' &&
    dataUrl.startsWith('data:image/') &&
    dataUrl.length > 100
  );
};

export default {
  compressImage,
  compressImageWithDetails,
  needsCompression,
  getImageSizeMB,
  compressImages,
  isValidDataUrl
};